/**
 * Mirror clipping — prevents vertices from crossing the mirror plane during
 * edit-mode transforms.
 *
 * When a mirror modifier has `clipping` enabled, vertices on the "original"
 * side of the mesh must not cross to the mirrored side. This module provides:
 *
 * 1. `collectClipPlanes` — resolves all active mirror clipping constraints
 *    for an object (both blend-file and runtime modifiers).
 * 2. `applyMirrorClipping` — clamps a vertex buffer in-place so no vertex
 *    crosses any active clip plane.
 */

import type { ModifierOverride } from "../app/storeTypes.ts";
import type {
  BlendMirrorModifier,
  BlendModifier,
} from "../blend-reader/types/BlendModifier.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A single axis-aligned clipping plane derived from a mirror modifier.
 *
 * Vertices with `position[axis] * sign < origin * sign` are clamped to
 * `origin` on that axis. In the common case (origin=0, sign=1) this means
 * "don't go below zero on this axis".
 *
 * `sign` is +1 when the original geometry lives on the positive side of the
 * plane, and −1 when it lives on the negative side. For own-origin mirrors
 * with origin = 0, sign = +1 by convention (Blender's standard).
 */
export interface ClipPlane {
  /** Component index: 0 = X, 1 = Y, 2 = Z */
  axis: number;
  /** Mirror origin on this axis (object-local space). */
  origin: number;
}

// ---------------------------------------------------------------------------
// Collecting clip planes
// ---------------------------------------------------------------------------

/**
 * Resolve the effective modifier list for an object, merging base modifiers
 * with runtime overrides.
 */
function resolveModifiers(
  baseModifiers: BlendModifier[],
  overrides: ModifierOverride[],
): BlendModifier[] {
  const maxLen = Math.max(baseModifiers.length, overrides.length);
  const result: BlendModifier[] = [];
  for (let i = 0; i < maxLen; i++) {
    const base = baseModifiers[i];
    const override = overrides[i];
    const mod = override?.runtimeModifier ?? base;
    if (!mod) continue;
    let merged = mod;
    if (override) {
      if (override.enabled !== undefined || override.props) {
        merged = { ...merged, ...(override.props ?? {}) } as typeof merged;
        if (override.enabled !== undefined)
          merged = { ...merged, enabled: override.enabled };
      }
    }
    result.push(merged);
  }
  return result;
}

/**
 * Collect all active clipping planes for an object.
 *
 * Walks the modifier stack (base + runtime overrides) and extracts a
 * `ClipPlane` for every enabled mirror axis that has `clipping: true`.
 *
 * For blend objects pass their `obj.modifiers`; for scene objects pass `[]`
 * (they have no base modifiers — everything is in overrides).
 */
export function collectClipPlanes(
  baseModifiers: BlendModifier[],
  overrides: ModifierOverride[],
): ClipPlane[] {
  const modifiers = resolveModifiers(baseModifiers, overrides);
  const planes: ClipPlane[] = [];

  for (const mod of modifiers) {
    if (!mod.enabled || mod.kind !== "mirror") continue;
    const m = mod as BlendMirrorModifier;
    if (!m.clipping) continue;

    const origin = m.mirrorOrigin ?? [0, 0, 0];

    if (m.axisX) planes.push({ axis: 0, origin: origin[0] });
    if (m.axisY) planes.push({ axis: 1, origin: origin[1] });
    if (m.axisZ) planes.push({ axis: 2, origin: origin[2] });
  }

  return planes;
}

// ---------------------------------------------------------------------------
// Applying clipping
// ---------------------------------------------------------------------------

/**
 * Clamp vertex positions in `vertices` so none cross any clip plane.
 *
 * Clipping is **bidirectional**: whichever side of the plane a vertex starts
 * on (determined from `sourceVertices`), it is prevented from crossing to the
 * other side. A vertex exactly on the plane stays pinned there.
 *
 * `sourceVertices` (pre-transform positions) is required to determine each
 * vertex's starting side. When omitted the function falls back to a
 * one-sided clamp (legacy behavior, should be avoided).
 *
 * Only the specified vertex indices are clamped (if `indices` is provided);
 * otherwise all vertices are clamped.
 *
 * Mutates `vertices` in place for performance during live drag.
 */
export function applyMirrorClipping(
  vertices: Float32Array,
  planes: ClipPlane[],
  indices?: Iterable<number>,
  sourceVertices?: Float32Array,
): void {
  if (planes.length === 0) return;

  if (indices) {
    for (const vi of indices) {
      const base = vi * 3;
      for (const p of planes) {
        clampVertex(vertices, base, p, sourceVertices);
      }
    }
  } else {
    const vertCount = vertices.length / 3;
    for (let vi = 0; vi < vertCount; vi++) {
      const base = vi * 3;
      for (const p of planes) {
        clampVertex(vertices, base, p, sourceVertices);
      }
    }
  }
}

/** Clamp a single vertex component so it doesn't cross the clip plane. */
function clampVertex(
  vertices: Float32Array,
  base: number,
  p: ClipPlane,
  sourceVertices?: Float32Array,
): void {
  const newVal = vertices[base + p.axis];
  if (sourceVertices) {
    const srcVal = sourceVertices[base + p.axis];
    // Vertex started on the positive side (or on the plane) — prevent crossing to negative
    if (srcVal >= p.origin && newVal < p.origin) {
      vertices[base + p.axis] = p.origin;
    }
    // Vertex started on the negative side (or on the plane) — prevent crossing to positive
    else if (srcVal <= p.origin && newVal > p.origin) {
      vertices[base + p.axis] = p.origin;
    }
  } else {
    // Legacy fallback: one-sided clamp
    if (newVal < p.origin) {
      vertices[base + p.axis] = p.origin;
    }
  }
}
